import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useQuickFilters from 'hooks/useQuickFilters';

interface DashboardFiltersContextType {
  // Default filters, saved on the dashboard (edited in edit mode)
  scope: FilterPlateType[];
  setScope: React.Dispatch<React.SetStateAction<FilterPlateType[]>>;
  // Run-time filters: the values the viewer picked for the pinned fields.
  // Not saved.
  drillDowns: FilterPlateType[];
  setDrillDowns: React.Dispatch<React.SetStateAction<FilterPlateType[]>>;
  // The dashboard's quick filters, and the ones the viewer has on
  quickFilters: LegacySavedFilter[];
  selectedQuickFilters: LegacySavedFilter[];
  setSelectedQuickFilters: React.Dispatch<
    React.SetStateAction<LegacySavedFilter[]>
  >;
  // What its gadgets are scoped to: the default filters and the selected
  // quick filters. Quick filters are a scope, as on a report, so a chart
  // grouped by a quick filter's field is filtered by it, not highlighted.
  appliedScope: FilterPlateType[];
  // Back to the saved scope, with no run-time or quick filters
  reset: () => void;
}

/**
 * Dashboard-level filters. Every dashboard gadget on the dashboard applies
 * them on top of its own filters.
 *
 * Undefined outside a dashboard, so a dashboard gadget rendered anywhere
 * else (Studio, entity apps, configure previews, popups) keeps only its own
 * filters.
 */
export const DashboardFiltersContext = React.createContext<
  DashboardFiltersContextType | undefined
>(undefined);

const DashboardFiltersProvider = ({
  dashboard,
  children,
}: {
  dashboard: DashboardType;
  children: JSX.Element | JSX.Element[];
}) => {
  const [scope, setScope] = useState<FilterPlateType[]>(
    () => dashboard.scope ?? [],
  );
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>([]);
  const quickFilters = useQuickFilters(`dashboard: ${dashboard.id}`);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    LegacySavedFilter[]
  >([]);

  // Keep the scope in sync with what is persisted
  useEffect(() => {
    setScope(dashboard.scope ?? []);
  }, [dashboard.scope]);

  // A quick filter edited or deleted applies as it now is, or not at all
  useEffect(() => {
    setSelectedQuickFilters((selected) =>
      selected
        .map((s) => quickFilters.find((q) => q.id === s.id))
        .filter((q): q is LegacySavedFilter => !!q),
    );
  }, [quickFilters]);

  const appliedScope = useMemo(
    () => [...scope, ...selectedQuickFilters.flatMap((qf) => qf.drillDowns)],
    [scope, selectedQuickFilters],
  );

  const reset = useCallback(() => {
    setScope(dashboard.scope ?? []);
    setDrillDowns([]);
    setSelectedQuickFilters([]);
  }, [dashboard.scope]);

  const value = useMemo(
    () => ({
      scope,
      setScope,
      drillDowns,
      setDrillDowns,
      quickFilters,
      selectedQuickFilters,
      setSelectedQuickFilters,
      appliedScope,
      reset,
    }),
    [
      appliedScope,
      drillDowns,
      quickFilters,
      reset,
      scope,
      selectedQuickFilters,
    ],
  );

  return (
    <DashboardFiltersContext.Provider value={value}>
      {children}
    </DashboardFiltersContext.Provider>
  );
};

export default DashboardFiltersProvider;
